import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/utils/api';
import { EmployeeSelect } from '../EmployeeSelect';
import { fmtRange } from '../agendaUtils';
import { AttachVehicle } from './AppointmentStatusBox';
import { FuelPicker, SignatureField, sigData, PhoneQrPanel } from './ReceptionFields';
import { DamageMapEditor, damageMapToPng } from './CarDamageMap';

const Photos = ({ woId }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const add = async (e) => {
    const files = [...(e.target.files || [])]; e.target.value = '';
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f, `reception_${Date.now()}_${f.name}`);
      try { await axios.post(`${API}/work-orders/${woId}/attachments`, fd); setList(l => [...l, URL.createObjectURL(f)]); }
      catch { toast.error(`Échec de l'envoi de ${f.name}`); }
    }
    setBusy(false);
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={add} data-testid="checkin-photo-input" />
      <div className="flex flex-wrap gap-2">
        {list.map((u, i) => <img key={i} src={u} alt="dégât" className="w-16 h-16 object-cover rounded-md border" data-testid={`checkin-photo-${i}`} />)}
        <button type="button" onClick={() => ref.current.click()} disabled={busy || !woId} data-testid="checkin-photo-btn"
          className="w-16 h-16 rounded-md border-2 border-dashed flex items-center justify-center text-slate-500 hover:border-slate-900 hover:text-slate-900">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

const CheckInForm = ({ appt, onDone }) => {
  const [f, setF] = useState({ mileage: '', fuel_level: '', keys_count: 1, items_left: '', damage_notes: '', signer_name: appt.client_name || '', assigned_employee_id: appt.assigned_employee_id || '' });
  const [damages, setDamages] = useState([]);
  const [saving, setSaving] = useState(false);
  const sig = useRef(null);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!f.mileage) { toast.error('Indiquez le kilométrage'); return; }
    setSaving(true);
    try {
      const signature = sigData(sig);
      const empChanged = f.assigned_employee_id !== (appt.assigned_employee_id || '');
      const damage_map = damages.length ? await damageMapToPng(damages) : '';
      await axios.post(`${API}/agenda/appointments/${appt.id}/check-in`, {
        ...f, assigned_employee_id: empChanged ? f.assigned_employee_id : null, mileage: parseInt(f.mileage, 10) || 0, signature,
        damages: damages.map(({ preview, ...d }) => d), damage_map,
      });
      toast.success('Véhicule réceptionné · fiche envoyée à l\'atelier');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid="checkin-form">
      <div><Label>Mécanicien affecté</Label>
        <EmployeeSelect value={f.assigned_employee_id} onChange={(id) => set('assigned_employee_id', id)} testid="checkin-employee-select" teamLabel="Non défini (équipe)" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Kilométrage *</Label><Input type="number" inputMode="numeric" value={f.mileage} onChange={e => set('mileage', e.target.value)} placeholder="km" data-testid="checkin-mileage-input" /></div>
        <div><Label>Clés reçues</Label>
          <div className="flex items-center gap-2 h-10">
            <Button type="button" size="icon" variant="outline" onClick={() => set('keys_count', Math.max(0, f.keys_count - 1))} data-testid="checkin-keys-minus"><Minus className="w-4 h-4" /></Button>
            <span className="w-6 text-center font-bold" data-testid="checkin-keys-count">{f.keys_count}</span>
            <Button type="button" size="icon" variant="outline" onClick={() => set('keys_count', f.keys_count + 1)} data-testid="checkin-keys-plus"><Plus className="w-4 h-4" /></Button>
          </div></div>
      </div>
      <div><Label>Niveau de carburant</Label>
        <FuelPicker value={f.fuel_level} onChange={v => set('fuel_level', v)} prefix="checkin" /></div>
      <div><Label>Dégâts existants</Label>
        <div className="mt-1"><DamageMapEditor woId={appt.work_order_id} value={damages} onChange={setDamages} /></div>
        <Textarea className="mt-2" rows={2} value={f.damage_notes} onChange={e => set('damage_notes', e.target.value)} placeholder="Remarques (facultatif)" data-testid="checkin-damage-input" /></div>
      <div><Label>Autres photos</Label><div className="mt-1"><Photos woId={appt.work_order_id} /></div></div>
      <div><Label>Objets laissés dans le véhicule</Label><Input value={f.items_left} onChange={e => set('items_left', e.target.value)} placeholder="Ex : siège enfant, roues d'hiver" data-testid="checkin-items-input" /></div>
      <SignatureField sigRef={sig} name={f.signer_name} onName={v => set('signer_name', v)} prefix="checkin" />
      <Button className="w-full h-12 text-base bg-[#0E1116] hover:bg-black text-[#FACC15]" onClick={submit} disabled={saving} data-testid="checkin-submit-btn">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Valider la réception'}
      </Button>
    </div>
  );
};

export const CheckInBody = ({ appt, onDone, mobile = false }) => {
  const [vehicleOk, setVehicleOk] = useState(!!appt?.vehicle_id);
  const [attached, setAttached] = useState(false);
  return (
    <>
      <div className="p-3 rounded-xl bg-slate-50 border text-sm" data-testid="checkin-summary">
        <p className="font-semibold">{appt.client_name}</p>
        <p className="text-slate-600">{fmtRange(appt.time, appt.end_time)} · {appt.intervention_type || 'Intervention'}</p>
        {vehicleOk && !attached && appt.vehicle_info && <p className="text-slate-600">{appt.vehicle_info}</p>}
        {attached && <p className="text-emerald-700">Véhicule ajouté</p>}
      </div>
      {vehicleOk ? <CheckInForm appt={appt} onDone={onDone} /> : <AttachVehicle appt={appt} mobile={mobile} onChanged={() => { setVehicleOk(true); setAttached(true); }} />}
    </>
  );
};

export const CheckInDialog = ({ appt, onClose, onDone }) => {
  if (!appt) return null;
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="checkin-dialog">
        <DialogHeader><DialogTitle>Réception du véhicule</DialogTitle></DialogHeader>
        <PhoneQrPanel apptId={appt.id} kind="checkin" path="/reception-mobile" title="Faire la réception avec le téléphone" doneMsg="Réception faite sur le téléphone" onDone={onDone}
          steps={['Scannez ce QR code avec le téléphone', 'Carte grise, km, carburant, clés, dégâts sur le dessin', 'Faites signer le client, puis Valider']} />
        <p className="text-xs text-center text-slate-500 uppercase tracking-wide">ou directement ici</p>
        <CheckInBody appt={appt} onDone={onDone} />
      </DialogContent>
    </Dialog>
  );
};
